import * as Dialog from '@radix-ui/react-dialog'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { useMutation } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { FxAvatarStack, FxButton, FxIcon } from '@/components/fx'
import { templatesApi } from '@/lib/api'
import { cn } from '@/lib/utils'
import { initialsFor, useCockpitDialog, type CockpitState } from './useCockpit'

/**
 * The band across the top of a template's workspace: where you are
 * (Übersicht / Projekt / name), what the name is and how to change it, who it
 * is shared with, and the two things people come here to do — share it and
 * add documents.
 *
 * It is the same header on all four tabs, so it belongs to `CockpitLayout`
 * and not to any of them.
 *
 * **At 390px the breadcrumb and both actions cannot all fit**, so the row
 * sheds in a fixed order, widest-first: the folder crumb goes (the title
 * already says which form this is), the back link keeps its arrow and drops
 * its word, the avatar stack goes, "Freigeben" moves into the overflow menu,
 * and "Dokumente hinzufügen" becomes its icon with an accessible name. What
 * never goes is the title and the primary action: the phone is where scans
 * are added.
 *
 * The header only puts `?dialog=share` / `?dialog=upload` in the query — the
 * canvas convention — so a dialog is linkable and a reload does not close it.
 * `CockpitLayout` mounts both dialogs off that parameter, once, for the
 * header and all four tabs.
 */

export function CockpitHeader({ cockpit }: { cockpit: CockpitState }) {
  const { template, folder, folderName, sharedWith, canWrite, canManage } = cockpit
  const { open } = useCockpitDialog()
  const [renaming, setRenaming] = useState(false)
  const [menuDialog, setMenuDialog] = useState<null | 'help' | 'archive'>(null)

  const title = template?.title ?? '…'
  const people = sharedWith.map(initialsFor)

  return (
    <header className="flex flex-wrap items-center gap-3 bg-surface px-4 py-3 shadow-raised sm:gap-4 sm:px-10 sm:py-4">
      {/* Breadcrumb. `Link` rather than a button: opening the Übersicht in a
          new tab is a normal thing to want from a workspace. */}
      <Link
        to="/"
        className="inline-flex shrink-0 items-center gap-2 text-body text-text no-underline hover:text-primary-text"
      >
        <FxIcon name="arrow_back" size={26} className="text-text-muted" />
        <span className="sr-only sm:not-sr-only">Übersicht</span>
      </Link>

      <span className="hidden text-text-muted md:inline" aria-hidden="true">
        /
      </span>
      {/* The project crumb links to the project list rather than to a folder
          route: `/projekte/:folderId` is not a route in this build, and a
          crumb that 404s is worse than one that lands a click away. */}
      <Link
        to="/projekte"
        className="hidden max-w-[180px] truncate text-body text-text no-underline hover:text-primary-text md:inline-block"
        title={folder ? folderName : undefined}
      >
        {folderName}
      </Link>
      <span className="hidden text-text-muted md:inline" aria-hidden="true">
        /
      </span>

      <FxIcon name="description" size={24} className="hidden shrink-0 text-primary sm:block" />

      {renaming && template ? (
        <RenameField
          cockpit={cockpit}
          onDone={() => setRenaming(false)}
        />
      ) : (
        <>
          <h1 className="min-w-0 flex-1 truncate text-card-name text-text sm:flex-none sm:text-tile-title">
            {title}
          </h1>
          {canWrite && template && (
            <button
              type="button"
              aria-label="Musterformular umbenennen"
              className="inline-flex shrink-0 rounded-sm p-1 text-text hover:text-primary-text"
              onClick={() => setRenaming(true)}
            >
              <FxIcon name="edit" size={22} />
            </button>
          )}
        </>
      )}

      <span className="hidden flex-1 md:block" />

      {people.length > 0 && (
        <button
          type="button"
          className="hidden items-center rounded-sm text-body text-text md:inline-flex"
          onClick={() => open('share')}
        >
          {/* The stack's own visuals are hidden from the accessibility tree
              and replaced by one sentence — see FxAvatarStack. */}
          <FxAvatarStack people={people} />
        </button>
      )}

      <FxButton
        variant="tonal"
        icon="person_add"
        className="hidden md:inline-flex"
        onClick={() => open('share')}
      >
        Freigeben
      </FxButton>

      <FxButton
        icon="upload"
        aria-label="Dokumente hinzufügen"
        className="shrink-0 max-sm:w-control max-sm:px-0"
        onClick={() => open('upload')}
      >
        <span className="max-sm:sr-only">Dokumente hinzufügen</span>
      </FxButton>

      <OverflowMenu
        canShare={people.length > 0 || canManage}
        canArchive={canWrite}
        onShare={() => open('share')}
        onHelp={() => setMenuDialog('help')}
        onArchive={() => setMenuDialog('archive')}
      />

      <HelpDialog open={menuDialog === 'help'} onClose={() => setMenuDialog(null)} />
      <ArchiveDialog
        templateId={cockpit.templateId}
        title={title}
        open={menuDialog === 'archive'}
        onClose={() => setMenuDialog(null)}
      />
    </header>
  )
}

/**
 * Renaming in place, as the pencil suggests, rather than in a dialog: it is
 * one short string and the person is already looking at it.
 *
 * Enter and blur save, Escape cancels — the three keys anyone tries. A failed
 * save keeps the field open with the reason under it, because that is where
 * the text still is; sending it to a toast would lose the edit.
 */
function RenameField({ cockpit, onDone }: { cockpit: CockpitState; onDone: () => void }) {
  const current = cockpit.template?.title ?? ''
  const [value, setValue] = useState(current)
  const input = useRef<HTMLInputElement>(null)
  const { rename } = cockpit

  useEffect(() => {
    input.current?.focus()
    input.current?.select()
  }, [])

  const save = () => {
    const next = value.trim()
    if (!next || next === current) {
      onDone()
      return
    }
    rename.mutate(next, { onSuccess: onDone })
  }

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1 sm:flex-none">
      <label className="sr-only" htmlFor="cockpit-rename">
        Name des Musterformulars
      </label>
      <input
        id="cockpit-rename"
        ref={input}
        value={value}
        disabled={rename.isPending}
        onChange={(event) => setValue(event.target.value)}
        onBlur={save}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            save()
          }
          if (event.key === 'Escape') {
            event.preventDefault()
            onDone()
          }
        }}
        className="h-control w-full min-w-0 rounded-md bg-ground px-3 text-card-name text-text sm:w-[320px] sm:text-tile-title"
      />
      {rename.isError && (
        <span role="alert" className="text-small text-danger-text">
          Der Name konnte nicht gespeichert werden.
        </span>
      )}
    </div>
  )
}

function OverflowMenu({
  canShare,
  canArchive,
  onShare,
  onHelp,
  onArchive,
}: {
  canShare: boolean
  canArchive: boolean
  onShare: () => void
  onHelp: () => void
  onArchive: () => void
}) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label="Weitere Aktionen"
          className="inline-flex shrink-0 rounded-sm p-1 text-text hover:text-primary-text"
        >
          <FxIcon name="more_vert" size={24} />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          collisionPadding={8}
          className="z-50 min-w-[240px] rounded-xl bg-surface p-2 text-body text-text shadow-overlay"
        >
          {/* Only below `md`, where the button itself is not in the row.
              `display:none` keeps it out of the menu's keyboard order too. */}
          {canShare && (
            <MenuItem icon="person_add" className="md:hidden" onSelect={onShare}>
              Freigeben
            </MenuItem>
          )}
          <MenuItem icon="help" onSelect={onHelp}>
            Hilfe zu dieser Seite
          </MenuItem>
          {canArchive && (
            <MenuItem icon="archive" onSelect={onArchive}>
              Archivieren
            </MenuItem>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

function MenuItem({
  icon,
  onSelect,
  className,
  children,
}: {
  icon: string
  onSelect: () => void
  className?: string
  children: React.ReactNode
}) {
  return (
    <DropdownMenu.Item
      onSelect={onSelect}
      className={cn(
        'flex cursor-default items-center gap-2 rounded-sm px-3 py-2 outline-none data-[highlighted]:bg-primary-subtle data-[highlighted]:text-primary-text',
        className,
      )}
    >
      <FxIcon name={icon} size={20} />
      {children}
    </DropdownMenu.Item>
  )
}

/** One dialog skin, in the design's overlay shape. Radix carries the focus
 *  trap, Escape and the labelled title. */
function Shell({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
}) {
  return (
    <Dialog.Root open={open} onOpenChange={(next) => !next && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-[#0000008c]" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(520px,calc(100vw-40px))] -translate-x-1/2 -translate-y-1/2 rounded-xl bg-surface p-5 shadow-overlay xs:p-8">
          <Dialog.Title className="mb-4 text-dialog-title text-text">{title}</Dialog.Title>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

/** The three sentences the Flutter cockpit's page help carried, in "Sie". */
function HelpDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Shell open={open} onClose={onClose} title="Formular-Cockpit">
      <ul className="flex list-disc flex-col gap-3 pl-5 text-body text-text">
        <li>
          Unter <b>Dokumente</b> fügen Sie ausgefüllte Exemplare hinzu — per Datei oder über den
          Handy-Link — und sehen, was die Analyse daraus gelesen hat.
        </li>
        <li>
          Unter <b>Vorlage</b> legen Sie fest, welche Felder erkannt werden und welche Layouts es
          gibt. Die Felder sind die Spalten im Export.
        </li>
        <li>
          Unter <b>Datensätze</b> holen Sie die Ergebnisse als Tabelle ab; unklare Werte prüfen Sie
          vorher unter „Unklarheiten beheben“.
        </li>
      </ul>
      <div className="mt-8 flex justify-end">
        <FxButton onClick={onClose}>Verstanden</FxButton>
      </div>
    </Shell>
  )
}

function ArchiveDialog({
  templateId,
  title,
  open,
  onClose,
}: {
  templateId: string
  title: string
  open: boolean
  onClose: () => void
}) {
  const navigate = useNavigate()
  const archive = useMutation({
    mutationFn: () => templatesApi.archive(templateId),
    onSuccess: () => {
      onClose()
      // The workspace of an archived form is not where anyone wants to be
      // left standing, and the Übersicht no longer lists it.
      navigate('/')
    },
  })

  return (
    <Shell open={open} onClose={onClose} title="Musterformular archivieren">
      {/* Archiving is reversible (`POST /v1/templates/:id/restore`) and the
          copy says so: a confirm that sounds final makes people keep forms
          they do not want. */}
      <p className="text-body text-text">
        „{title}“ wird ins Archiv gelegt. Die Dokumente bleiben erhalten, und Sie können das
        Formular jederzeit wiederherstellen.
      </p>
      {archive.isError && (
        <p className="mt-4 text-small text-danger-text" role="alert">
          Das Archivieren hat nicht geklappt. Bitte noch einmal versuchen.
        </p>
      )}
      <div className="mt-8 flex justify-end gap-4">
        <FxButton variant="outlined" onClick={onClose}>
          Abbrechen
        </FxButton>
        <FxButton disabled={archive.isPending} onClick={() => archive.mutate()}>
          Archivieren
        </FxButton>
      </div>
    </Shell>
  )
}
